import { isBoolean } from "lodash";

import type { FieldOrGetter } from "./helpers";
import { getPathGetter } from "./helpers";
import { isNumber, isRealString } from "./text";

/** Always returns 0, does not change the order when used as a sorting function */
export function keepOriginalOrder() {
  return 0;
}

export function alphabetize<Shape>(fieldOrGetter: FieldOrGetter<Shape>) {
  const fieldGetter = getPathGetter(fieldOrGetter);
  return (a: Shape, b: Shape) =>
    stringComparator(fieldGetter(a), fieldGetter(b));
}

type Comparand = any;

/**
 * Sorts populated values in ascending/descending directions putting null /
 * undefined values at the end. Set `descending` to true if sorting values
 * in descending order.
 */
export function compare(a: Comparand, b: Comparand, descending?: boolean) {
  if (a == null) {
    return b == null ? 0 : 1;
  } else if (b == null) {
    return -1;
  }

  // `|| 0` to replace -0 which JS apparently treats differently from 0
  return compareAscending(a, b) * (descending ? -1 : 1) || 0;
}

const getNumber = (val: Comparand) =>
  isNumber(val) ? Number(val) : isRealString(val) ? parseFloat(val) : NaN;

/**
 * `compareAscending` is not exported because it doesn't handle undefined/null
 */
function compareAscending(a: Comparand, b: Comparand) {
  const aNum = getNumber(a);
  const bNum = getNumber(b);

  if (!Number.isNaN(aNum)) {
    // give priority to numbers, so sort a before b if b is not a number
    return Number.isNaN(bNum) ? -1 : aNum - bNum;
  } else if (!Number.isNaN(bNum)) {
    // give priority to numbers, so sort b before a if a is not a number
    return 1;
  } else if (a?.localeCompare) {
    return a.localeCompare(b);
  } else if (b?.localeCompare) {
    // need to negate because we're sorting the operands backwards
    return -b.localeCompare(a);
  } else if (isBoolean(a) && isBoolean(b)) {
    // booleans evaluate as true = 1 / false = 0, so are sorted false, then true
    // but it's generally preferred to sort booleans with true first
    return Number(b) - Number(a);
  } else {
    const aVal = a ?? 0;
    const bVal = b ?? 0;

    return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
  }
}

export const stringComparator = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true });
